/**
 * VIVA HUB CRM — lead list and board
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import HubLayout from '../components/hub/HubLayout'
import CrmHelpLink from '../components/hub/crm/CrmHelpLink'
import CrmSettingsLink from '../components/hub/crm/CrmSettingsLink'
import CrmLeadsBoard from '../components/hub/crm/CrmLeadsBoard'
import CrmLeadsList from '../components/hub/crm/CrmLeadsList'
import CrmIdleLegend from '../components/hub/crm/CrmIdleLegend'
import { Button } from '@/components/ui/button'
import { hubCrmAPI } from '../utils/hub/api'
import {
  CRM_MANUAL_CREATE_SOURCES,
  LEAD_SOURCE_LABELS,
  LEAD_STATUS_LABELS,
} from '../lib/crm-display'

const STATUS_OPTIONS = Object.keys(LEAD_STATUS_LABELS).filter((s) => s !== 'archived')
const PAGE_SIZE = 25
const IDLE_FETCH_LIMIT = 200
const BOARD_FETCH_LIMIT = 200

function parsePageParam(value) {
  const p = parseInt(value || '1', 10)
  return Number.isNaN(p) || p < 1 ? 1 : p
}

function CrmViewTabs({ view, onChange, className = 'mb-4 border-b border-gray-200' }) {
  const tabClass = (active) =>
    [
      'px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
      active
        ? 'border-green-600 text-green-800'
        : 'border-transparent text-gray-500 hover:text-gray-800 hover:border-gray-300',
    ].join(' ')

  return (
    <nav className={className} aria-label="CRM views">
      <div className="flex gap-1">
        <button type="button" className={tabClass(view === 'list')} onClick={() => onChange('list')}>
          List
        </button>
        <button type="button" className={tabClass(view === 'board')} onClick={() => onChange('board')}>
          Board
        </button>
      </div>
    </nav>
  )
}

export default function HubCRM() {
  const [searchParams, setSearchParams] = useSearchParams()
  const view = searchParams.get('view') === 'board' ? 'board' : 'list'
  const [leads, setLeads] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(() => parsePageParam(searchParams.get('page')))
  const [loading, setLoading] = useState(true)
  const [refreshing, setRefreshing] = useState(false)
  const [error, setError] = useState('')
  const [showNewLead, setShowNewLead] = useState(false)
  const [creating, setCreating] = useState(false)

  const [filters, setFilters] = useState({
    assigned: searchParams.get('assigned') || 'all',
    status: searchParams.get('status') || '',
    source: searchParams.get('source') || '',
    idle: searchParams.get('idle') === '1',
    has_intake: searchParams.get('has_intake') === '1',
    search: searchParams.get('search') || '',
    include_archived: searchParams.get('include_archived') === '1',
  })

  const [newLead, setNewLead] = useState({
    display_name: '',
    phone: '',
    email: '',
    source: 'whatsapp',
    note: '',
  })

  const setView = (nextView) => {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (nextView === 'board') next.set('view', 'board')
        else next.delete('view')
        if (nextView === 'board') next.delete('page')
        return next
      },
      { replace: true }
    )
    if (nextView === 'board') setPage(1)
  }

  const updateFilters = (patch) => {
    setPage(1)
    setFilters((f) => ({ ...f, ...patch }))
  }

  const loadLeads = useCallback(
    async ({ refresh = false } = {}) => {
      if (refresh) setRefreshing(true)
      else setLoading(true)
      setError('')
      try {
        const params = {}
        if (filters.assigned && filters.assigned !== 'all') params.assigned = filters.assigned
        if (filters.status) params.status = filters.status
        if (filters.source) params.source = filters.source
        if (filters.idle) params.idle = '1'
        if (filters.has_intake) params.has_intake = '1'
        if (filters.search) params.search = filters.search
        if (filters.include_archived) params.include_archived = '1'

        const useBoardFetch = view === 'board'
        if (filters.idle || useBoardFetch) {
          params.limit = String(useBoardFetch ? BOARD_FETCH_LIMIT : IDLE_FETCH_LIMIT)
          params.offset = '0'
        } else {
          params.limit = String(PAGE_SIZE)
          params.offset = String((page - 1) * PAGE_SIZE)
        }

        const result = await hubCrmAPI.listLeads(params)
        setLeads(result.leads || [])
        if (filters.idle || useBoardFetch) {
          setTotal(result.total ?? (result.leads || []).length)
        } else {
          setTotal(result.total ?? (result.leads || []).length)
        }
      } catch (err) {
        setError(err.message || 'Failed to load cases')
      } finally {
        setLoading(false)
        setRefreshing(false)
      }
    },
    [filters, page, view]
  )

  useEffect(() => {
    loadLeads()
  }, [loadLeads])

  useEffect(() => {
    const next = new URLSearchParams()
    if (view === 'board') next.set('view', 'board')
    if (filters.assigned !== 'all') next.set('assigned', filters.assigned)
    if (filters.status) next.set('status', filters.status)
    if (filters.source) next.set('source', filters.source)
    if (filters.idle) next.set('idle', '1')
    if (filters.has_intake) next.set('has_intake', '1')
    if (filters.search) next.set('search', filters.search)
    if (filters.include_archived) next.set('include_archived', '1')
    if (view === 'list' && !filters.idle && page > 1) next.set('page', String(page))
    setSearchParams(next, { replace: true })
  }, [filters, page, view, setSearchParams])

  const pageCount = useMemo(
    () => (filters.idle || view === 'board' ? 1 : Math.max(1, Math.ceil(total / PAGE_SIZE))),
    [filters.idle, view, total]
  )

  const pagination = useMemo(() => {
    if (filters.idle || view === 'board' || total === 0) {
      const count = leads.length
      return {
        page: 0,
        pageCount: 1,
        totalItems: count,
        showingFrom: count === 0 ? 0 : 1,
        showingTo: count,
        goToPage: () => {},
        goPrev: () => {},
        goNext: () => {},
        hasPrev: false,
        hasNext: false,
        showPagination: false,
      }
    }

    const offset = (page - 1) * PAGE_SIZE
    const showingFrom = offset + 1
    const showingTo = offset + leads.length

    return {
      page: page - 1,
      pageCount,
      totalItems: total,
      showingFrom,
      showingTo,
      goToPage: (p) => setPage(p + 1),
      goPrev: () => setPage((p) => Math.max(1, p - 1)),
      goNext: () => setPage((p) => Math.min(pageCount, p + 1)),
      hasPrev: page > 1,
      hasNext: page < pageCount,
      showPagination: pageCount > 1,
    }
  }, [filters.idle, view, leads.length, page, pageCount, total])

  const boardCapped = view === 'board' && total > leads.length

  const handleCreateLead = async (e) => {
    e.preventDefault()
    setCreating(true)
    setError('')
    try {
      const result = await hubCrmAPI.createLead({
        display_name: newLead.display_name.trim(),
        phone: newLead.phone.trim() || undefined,
        email: newLead.email.trim() || undefined,
        source: newLead.source,
        note: newLead.note.trim() || undefined,
      })
      setShowNewLead(false)
      setNewLead({ display_name: '', phone: '', email: '', source: 'whatsapp', note: '' })
      if (result.lead_id) {
        window.location.href = `/hub/crm/${result.lead_id}`
      } else {
        await loadLeads()
      }
    } catch (err) {
      setError(err.message || 'Failed to create case')
    } finally {
      setCreating(false)
    }
  }

  const showEmpty = !loading && !refreshing && leads.length === 0
  const showContent = !loading && leads.length > 0

  return (
    <HubLayout appName="CRM">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <p className="text-sm text-gray-600 max-w-xl">
          Manage prospective clients from WhatsApp, phone, and website forms.
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <CrmHelpLink />
          <CrmSettingsLink />
          <Button type="button" variant="success" size="sm" onClick={() => setShowNewLead(true)}>
            New case
          </Button>
        </div>
      </div>

      <div className="mb-4 flex flex-wrap gap-2">
        <select
          className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white"
          value={filters.assigned}
          onChange={(e) => updateFilters({ assigned: e.target.value })}
        >
          <option value="all">All assignees</option>
          <option value="me">My cases</option>
          <option value="unassigned">Unassigned</option>
        </select>
        <select
          className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white"
          value={filters.status}
          onChange={(e) => updateFilters({ status: e.target.value })}
        >
          <option value="">All statuses</option>
          {STATUS_OPTIONS.map((s) => (
            <option key={s} value={s}>
              {LEAD_STATUS_LABELS[s]}
            </option>
          ))}
        </select>
        <select
          className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white"
          value={filters.source}
          onChange={(e) => updateFilters({ source: e.target.value })}
        >
          <option value="">All sources</option>
          {Object.entries(LEAD_SOURCE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
        <input
          type="search"
          placeholder="Search name, email, phone"
          className="px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white min-w-[200px]"
          value={filters.search}
          onChange={(e) => updateFilters({ search: e.target.value })}
        />
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.idle}
            onChange={(e) => updateFilters({ idle: e.target.checked })}
            className="rounded border-gray-300 text-green-600"
          />
          Idle only
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.has_intake}
            onChange={(e) => updateFilters({ has_intake: e.target.checked })}
            className="rounded border-gray-300 text-green-600"
          />
          Has intake form
        </label>
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={filters.include_archived}
            onChange={(e) => updateFilters({ include_archived: e.target.checked })}
            className="rounded border-gray-300 text-green-600"
          />
          Include archived
        </label>
        <button
          type="button"
          onClick={() => loadLeads({ refresh: true })}
          disabled={loading || refreshing}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm border border-gray-200 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:pointer-events-none"
          aria-label="Refresh cases"
        >
          <svg
            className={`size-4 shrink-0 ${refreshing ? 'animate-spin' : ''}`}
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth="2"
            stroke="currentColor"
            aria-hidden
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99"
            />
          </svg>
          {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      <CrmIdleLegend>
        <CrmViewTabs view={view} onChange={setView} className="border-0 mb-0" />
      </CrmIdleLegend>

      {error && (
        <div className="rounded-md bg-red-50 p-4 mb-4">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      {loading && leads.length === 0 && <p className="text-gray-600">Loading cases...</p>}

      {showEmpty && <p className="text-gray-500 text-sm">No cases found.</p>}

      {showContent && view === 'list' && (
        <CrmLeadsList leads={leads} pagination={pagination} refreshing={refreshing} />
      )}

      {showContent && view === 'board' && (
        <div className={refreshing ? 'opacity-60 pointer-events-none' : ''}>
          <CrmLeadsBoard
            leads={leads}
            statusFilter={filters.status}
            includeArchived={filters.include_archived}
            capped={boardCapped}
          />
        </div>
      )}

      {showNewLead && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="new-lead-title"
        >
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <h2 id="new-lead-title" className="text-lg font-semibold text-gray-900 mb-4">
              New case
            </h2>
            <form onSubmit={handleCreateLead} className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  required
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newLead.display_name}
                  onChange={(e) =>
                    setNewLead((n) => ({ ...n, display_name: e.target.value }))
                  }
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                <input
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newLead.phone}
                  onChange={(e) => setNewLead((n) => ({ ...n, phone: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newLead.email}
                  onChange={(e) => setNewLead((n) => ({ ...n, email: e.target.value }))}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Source</label>
                <select
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newLead.source}
                  onChange={(e) => setNewLead((n) => ({ ...n, source: e.target.value }))}
                >
                  {CRM_MANUAL_CREATE_SOURCES.map((value) => (
                    <option key={value} value={value}>
                      {LEAD_SOURCE_LABELS[value]}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Note</label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm"
                  value={newLead.note}
                  onChange={(e) => setNewLead((n) => ({ ...n, note: e.target.value }))}
                />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button
                  type="button"
                  className="px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-md"
                  onClick={() => setShowNewLead(false)}
                >
                  Cancel
                </button>
                <Button type="submit" variant="success" size="sm" disabled={creating}>
                  {creating ? 'Saving...' : 'Create case'}
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}
    </HubLayout>
  )
}
